import * as SentryBrowser from '@sentry/browser';
import { reportToSentry } from '@sonnen/web-utils';
import { Component, ErrorInfo, ReactNode } from 'react';
import styles from './SentryErrorBoundary.module.css';

/**
 * Props for the SentryErrorBoundary component
 */
interface SentryErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional fallback UI to show when an error occurs
   */
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo) => ReactNode);
  /**
   * Optional callback when an error is caught
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * Tag to identify this error boundary in Sentry
   */
  boundaryName?: string;
}

interface SentryErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component that catches React errors and reports them to Sentry.
 *
 * Follows sonnen-app pattern where the app is wrapped with Sentry.wrap(App).
 * Since we're using @sentry/browser (not @sentry/react), we create a custom
 * error boundary that achieves the same functionality.
 *
 * Pattern mirrored from sonnen-app app.tsx:
 * - Wraps the entire app to catch unhandled React errors
 * - Reports errors to Sentry with component stack
 * - Provides fallback UI option
 *
 * @example
 * // In main.tsx - wrap the entire app
 * <SentryErrorBoundary fallback={<ErrorFallback />}>
 *   <App />
 * </SentryErrorBoundary>
 *
 * @example
 * // Wrap specific components
 * <SentryErrorBoundary boundaryName="checkout-flow">
 *   <CheckoutForm />
 * </SentryErrorBoundary>
 */
export class SentryErrorBoundary extends Component<
  SentryErrorBoundaryProps,
  SentryErrorBoundaryState
> {
  constructor(props: SentryErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<SentryErrorBoundaryState> {
    return { hasError: true, error };
  }

  static isChunkLoadError(error: Error): boolean {
    return (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      // Safari / iOS WKWebView reports chunk fetch failures as a generic "Load failed"
      error.message === 'Load failed' ||
      error.name === 'ChunkLoadError'
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError, boundaryName = 'root' } = this.props;

    // Stale chunk after a new deployment — reload once to get the fresh bundle.
    // A session-storage flag prevents infinite reload loops if the new chunk also fails.
    if (SentryErrorBoundary.isChunkLoadError(error)) {
      const reloadKey = 'chunk_reload_attempted';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
        return;
      }
    }

    // Update state with error info
    this.setState({ errorInfo });

    // Report to Sentry with full context
    reportToSentry(`React Error Boundary: ${boundaryName}`, {
      tag: 'react-error-boundary',
      level: 'error',
      context: {
        boundaryName,
        componentStack: errorInfo.componentStack,
        errorMessage: error.message,
        errorName: error.name,
        errorHandled: 'true',
      },
    });

    // Also set the component stack as extra context in Sentry
    SentryBrowser.withScope((scope) => {
      scope.setExtra('componentStack', errorInfo.componentStack);
      scope.setTag('error.boundary', boundaryName);
    });

    // Call optional error callback
    if (onError) {
      onError(error, errorInfo);
    }
  }

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // If fallback is a function, call it with error details
      if (typeof fallback === 'function') {
        return fallback(error, errorInfo!);
      }

      // If fallback is provided as element, render it
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <h1 className={styles.title}>Something went wrong</h1>
            <p className={styles.message}>
              We&apos;re sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <button className={styles.button} onClick={() => window.location.reload()}>
              Refresh Page
            </button>
            {import.meta.env.DEV && (
              <details className={styles.details}>
                <summary className={styles.summary}>Error Details (Development Only)</summary>
                <pre className={styles.errorText}>{error.message}</pre>
                <pre className={styles.stackText}>{error.stack}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Props interface for withSentryErrorBoundary HOC
 */
type WithSentryErrorBoundaryOptions = Omit<SentryErrorBoundaryProps, 'children'>;

/**
 * Higher-order component to wrap a component with Sentry error boundary.
 *
 * Mirrors sonnen-app's Sentry.wrap(App) pattern for web.
 *
 * @param WrappedComponent - The component to wrap
 * @param options - Error boundary options
 *
 * @example
 * // Wrap App component (like sonnen-app's Sentry.wrap(App))
 * const AppWithSentry = withSentryErrorBoundary(App);
 * export default AppWithSentry;
 *
 * @example
 * // With custom options
 * const AppWithSentry = withSentryErrorBoundary(App, {
 *   boundaryName: 'app-root',
 *   fallback: <CustomErrorPage />,
 * });
 */
export function withSentryErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: WithSentryErrorBoundaryOptions,
): React.FC<P> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithErrorBoundary: React.FC<P> = (props) => (
    <SentryErrorBoundary
      fallback={options?.fallback}
      onError={options?.onError}
      boundaryName={options?.boundaryName}
    >
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <WrappedComponent {...props} />
    </SentryErrorBoundary>
  );

  ComponentWithErrorBoundary.displayName = `withSentryErrorBoundary(${displayName})`;

  return ComponentWithErrorBoundary;
}

export default SentryErrorBoundary;
